import { FlowState, QueryItem } from "../@types";
import { ALL_SUGGESTIONS, FLOW_STATES } from "../consts";
import { stringValueIsProperlyQuoted, valueIsKeywordOperator } from "../utils";
import { createNewOption } from "./createNewOptionsUtils";

export const nextFlowState = (
  currentState: FlowState,
  value?: string
): FlowState => {
  switch (currentState) {
    case FLOW_STATES.ALL_KEYWORDS:
      if (valueIsKeywordOperator(value ?? "")) return FLOW_STATES.ALL_KEYWORDS;
      if (value?.endsWith(":") && ALL_SUGGESTIONS[value])
        return FLOW_STATES.SINGLE_OR_MULTI_VALUE;
      return FLOW_STATES.ALL_KEYWORDS;
    case FLOW_STATES.SINGLE_OR_MULTI_VALUE:
      if (value === "[") return FLOW_STATES.FIRST_MULTI_VALUE;
      return FLOW_STATES.ALL_KEYWORDS;
    case FLOW_STATES.FIRST_MULTI_VALUE:
      if (value === "]") return FLOW_STATES.ALL_KEYWORDS;
      return FLOW_STATES.MULTI_VALUE_OR_RANGE;
    case FLOW_STATES.MULTI_VALUE_OR_RANGE:
      if (value === "]") return FLOW_STATES.ALL_KEYWORDS;
      if (value === "TO") return FLOW_STATES.RANGETOVALUE;
      return FLOW_STATES.MULTI_VALUE;
    case FLOW_STATES.RANGETOVALUE:
      return FLOW_STATES.CLOSE_RANGE;
    case FLOW_STATES.CLOSE_RANGE:
      return FLOW_STATES.ALL_KEYWORDS;
    case FLOW_STATES.MULTI_VALUE:
      if (value === "]") return FLOW_STATES.ALL_KEYWORDS;
      return FLOW_STATES.MULTI_VALUE;
    default:
      return FLOW_STATES.ALL_KEYWORDS;
  }
};

export const flowStateIsCreatable = (flowState: FlowState) =>
  [
    FLOW_STATES.SINGLE_OR_MULTI_VALUE,
    FLOW_STATES.FIRST_MULTI_VALUE,
    FLOW_STATES.MULTI_VALUE,
    FLOW_STATES.MULTI_VALUE_OR_RANGE,
    FLOW_STATES.RANGETOVALUE
  ].includes(flowState);

export const stringIsCreatable = (
  inputValue: string,
  flowState: FlowState,
  group?: readonly QueryItem[]
) => {
  const keyword = group?.[0]?.value;
  const trimmedInputValue = inputValue.trim().toUpperCase();

  if (!trimmedInputValue) return false;

  const keywordDoesntAllowInAndRange =
    flowState === FLOW_STATES.SINGLE_OR_MULTI_VALUE &&
    keyword &&
    ALL_SUGGESTIONS[keyword] &&
    !ALL_SUGGESTIONS[keyword].operators?.IN &&
    !ALL_SUGGESTIONS[keyword].operators?.RANGE;
  if (keywordDoesntAllowInAndRange && trimmedInputValue === "[") return false;

  const keywordDoesntAllowRange =
    flowState === FLOW_STATES.MULTI_VALUE_OR_RANGE &&
    keyword &&
    ALL_SUGGESTIONS[keyword] &&
    !ALL_SUGGESTIONS[keyword].operators?.RANGE;
  if (keywordDoesntAllowRange && trimmedInputValue === "TO") return false;

  if (flowState === FLOW_STATES.RANGETOVALUE && trimmedInputValue === "]")
    return false;

  if (flowState === FLOW_STATES.CLOSE_RANGE && trimmedInputValue !== "]")
    return false;

  const keywordDoesntAllowWildcard =
    flowStateIsCreatable(flowState) &&
    keyword &&
    ALL_SUGGESTIONS[keyword] &&
    !ALL_SUGGESTIONS[keyword].operators?.EQ_WILDCARD &&
    trimmedInputValue.includes("*");
  if (keywordDoesntAllowWildcard) return false;

  return true;
};

export const optionShouldHaveGroup = (option: QueryItem) =>
  option.state === FLOW_STATES.ALL_KEYWORDS;

export const stringIsANonKeywordArgument = (
  value: string,
  flowState: FlowState
) =>
  flowState === FLOW_STATES.ALL_KEYWORDS &&
  !ALL_SUGGESTIONS[value.trim()] &&
  !valueIsKeywordOperator(value) &&
  stringValueIsProperlyQuoted(value);

export const createOptionFromLiveString = (
  value: string,
  flowState: FlowState
) => {
  const trimmedNewValue = value.trim();

  if (!trimmedNewValue) {
    return undefined;
  }

  if (flowState === FLOW_STATES.ALL_KEYWORDS) {
    if (!stringValueIsProperlyQuoted(trimmedNewValue)) {
      return undefined;
    }

    const upperCasedValue = value.toUpperCase();
    const newValueIsOperator = ["AND ", "OR ", "NOT ", "(", ")"].includes(
      upperCasedValue.trimLeft()
    );
    if (newValueIsOperator) {
      return createNewOption({
        value: upperCasedValue,
        flowState,
        useValueAsItemKey: true
      });
    }

    if (
      value.at(-1) === " " ||
      (value.at(-1) === ":" && ALL_SUGGESTIONS[value])
    ) {
      return createNewOption({
        value: trimmedNewValue,
        flowState,
        useValueAsItemKey: true
      });
    }
  }

  if (flowState === FLOW_STATES.CLOSE_RANGE && trimmedNewValue === "]") {
    return createNewOption({
      value: trimmedNewValue,
      flowState,
      useValueAsItemKey: true
    });
  }

  if (
    [
      FLOW_STATES.FIRST_MULTI_VALUE,
      FLOW_STATES.MULTI_VALUE_OR_RANGE,
      FLOW_STATES.MULTI_VALUE,
      FLOW_STATES.RANGETOVALUE
    ].includes(flowState) &&
    trimmedNewValue.endsWith("]")
  ) {
    const slicedValue = trimmedNewValue.slice(0, -1).trim();
    return createNewOption({
      value: trimmedNewValue,
      flowState,
      complexValue: [...(slicedValue ? [slicedValue] : []), "]"],
      useValueAsItemKey: true
    });
  }

  const valueIsOpenBracket =
    flowState === FLOW_STATES.SINGLE_OR_MULTI_VALUE && trimmedNewValue === "[";
  const valueIsToKeyword =
    flowState === FLOW_STATES.MULTI_VALUE_OR_RANGE &&
    trimmedNewValue.toUpperCase() === "TO" &&
    value.endsWith(" ");
  const valueCanBeCreated =
    flowStateIsCreatable(flowState) &&
    stringValueIsProperlyQuoted(trimmedNewValue) &&
    value.endsWith(" ");
  if (valueIsOpenBracket || valueIsToKeyword || valueCanBeCreated) {
    return createNewOption({
      value: trimmedNewValue.toUpperCase() === "TO" ? "TO" : trimmedNewValue,
      flowState,
      useValueAsItemKey: true
    });
  }

  const valueShouldBecomeMultiValue =
    [
      FLOW_STATES.FIRST_MULTI_VALUE,
      FLOW_STATES.MULTI_VALUE,
      FLOW_STATES.MULTI_VALUE_OR_RANGE
    ].includes(flowState) &&
    stringValueIsProperlyQuoted(trimmedNewValue) &&
    trimmedNewValue.endsWith(",");
  if (valueShouldBecomeMultiValue) {
    return createNewOption({
      value: trimmedNewValue.slice(0, -1),
      flowState,
      useValueAsItemKey: true
    });
  }

  return undefined;
};
